"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { ArrowRight, Menu, X, ShoppingCart } from "lucide-react";
import { useCart } from "@/store/cart";
import { AccountDropdown } from "./AccountDropdown";
import { cn } from "@/lib/utils";

const NAV_LINKS = [
  { label: "Artists",    href: "/artists" },
  { label: "About",      href: "/about" },
  { label: "Blog",       href: "/blog" },
];

export function Navigation() {
  const pathname = usePathname();
  const { cart, openCart } = useCart();
  const mobileDialogRef = useRef<HTMLDialogElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const cartCount = cart?.totalQuantity ?? 0;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const dialog = mobileDialogRef.current;
    if (!dialog) return;

    if (mobileOpen && !dialog.open) dialog.showModal();
    if (!mobileOpen && dialog.open) dialog.close();

    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  function closeMenus() {
    setMobileOpen(false);
  }

  return (
    <>
      <header
        className={cn(
          "sticky top-0 z-40 w-full transition-all duration-200",
          scrolled
            ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#2A2A2A]"
            : "bg-[#0A0A0A]"
        )}
      >
        <div className="container-main">
          <div className="flex items-center justify-between h-16 md:h-18">
            {/* Logo */}
            <Link
              href="/"
              className="text-lg sm:text-xl font-bold tracking-tight text-white whitespace-nowrap"
              style={{ fontFamily: "var(--font-display)" }}
            >
              AURORA<span style={{ color: "#E8C547" }}>STRINGS</span>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-6">
              <Link
                href="/shop"
                onClick={closeMenus}
                className={cn(
                  "text-sm font-semibold transition-colors hover:text-[#E8C547]",
                  pathname.startsWith("/shop") ? "text-[#E8C547]" : "text-[#F5F5F5]"
                )}
              >
                Shop all strings
              </Link>

              {/* Build Your Set — first-class CTA */}
              <Link
                href="/configure"
                onClick={closeMenus}
                className={cn(
                  "text-sm font-semibold transition-colors",
                  pathname === "/configure"
                    ? "text-[#E8C547]"
                    : "text-[#E8C547] hover:text-[#F0D060]"
                )}
              >
                Build your set
              </Link>

              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={closeMenus}
                  className={cn(
                    "text-sm font-medium transition-colors hover:text-[#E8C547]",
                    pathname === link.href ? "text-[#E8C547]" : "text-[#9A9A9A]"
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right icons */}
            <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-3">
              <AccountDropdown />

              <Link
                href="/cart"
                className={cn(
                  "hidden md:inline-flex text-sm font-medium transition-colors hover:text-[#E8C547]",
                  pathname === "/cart" ? "text-[#E8C547]" : "text-[#9A9A9A]"
                )}
              >
                View Cart
              </Link>

              <button
                aria-label={`Cart (${cartCount} items)`}
                onClick={openCart}
                className="relative p-2 text-[#9A9A9A] hover:text-[#F5F5F5] transition-colors"
              >
                <ShoppingCart size={20} />
                {cartCount > 0 && (
                  <span
                    className="absolute -top-0.5 -right-0.5 flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded-full"
                    style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
                  >
                    {cartCount > 9 ? "9+" : cartCount}
                  </span>
                )}
              </button>

              {/* Hamburger (mobile) */}
              <button
                className="lg:hidden p-2 text-[#9A9A9A] hover:text-[#F5F5F5] transition-colors"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
                aria-expanded={mobileOpen}
                aria-controls="mobile-navigation"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      <dialog
        ref={mobileDialogRef}
        id="mobile-navigation"
        aria-label="Site navigation"
        onClose={closeMenus}
        className="m-0 h-dvh max-h-none w-screen max-w-none border-0 bg-[#0A0A0A] p-0 text-[#F5F5F5] backdrop:bg-black/60 lg:hidden"
      >
        <button
          type="button"
          onClick={closeMenus}
          className="absolute right-4 top-4 p-3 text-[#9A9A9A] transition-colors hover:text-[#F5F5F5]"
          aria-label="Close menu"
        >
          <X size={24} />
        </button>
        <nav className="container-main flex min-h-full flex-col justify-center py-16">
            <Link
              href="/configure"
              onClick={closeMenus}
              className="group inline-flex items-center gap-2 py-3 text-lg font-semibold border-b border-[#2A2A2A]"
              style={{ color: "#E8C547" }}
            >
              Build your set
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
            </Link>

            <Link
              href="/shop"
              onClick={closeMenus}
              className="py-3 text-lg font-semibold border-b border-[#2A2A2A] text-[#F5F5F5] hover:text-[#E8C547] transition-colors"
            >
              Shop all strings
            </Link>

            <Link
              href="/cart"
              onClick={closeMenus}
              className="py-3 text-lg font-semibold border-b border-[#2A2A2A] text-[#F5F5F5] hover:text-[#E8C547] transition-colors"
            >
              View cart
            </Link>

            <div className="border-t border-[#2A2A2A] mt-4 pt-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={closeMenus}
                  className="py-2.5 text-base text-[#9A9A9A] hover:text-[#F5F5F5] transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
        </nav>
      </dialog>
    </>
  );
}
